import React, { ReactElement } from "react";
import styled from "styled-components";
import DropDownArrowIcon from "icons/ArrowDownIcon";
import media from "../../utils/media";
import { ScriptDirection } from "../../api/typedefs/Bible";

const noOp = () => {};

interface StyledDropdownContentProps {
  isOpen: boolean;
  bottom?: string | number;
  theme: {
    scriptDirection: ScriptDirection;
  };
}

interface DropdownProps {
  children: ReactElement | ReactElement[];
  isOpen?: boolean;
  toggle?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

interface DropdownContentProps {
  id?: string;
  style?: object;
  isOpen?: boolean;
  bottom?: string | number;
}

interface DropdownTriggerProps extends DropdownProps {
  arrow: boolean;
}

const StyledDropdownContent = styled.div<StyledDropdownContentProps>`
  display: ${props => (props.isOpen ? "block" : "none")};
  border-radius: 16px;
  padding: 12px 0;
  background: ${({ theme }) =>
    theme.darkMode ? theme.computedPrimary : "white"};
  overflow-y: auto;
  left: 18px;
  right: 18px;
  bottom: 0;
  top: 68px;
  position: absolute;
  -webkit-overflow-scrolling: touch;
  direction: ${props => props.theme.scriptDirection || "ltr"};
  max-width: calc(100vw - 38px);
  width: 100%;
  border: 1px solid ${props => (props.theme.darkMode ? "#5D5D5D" : "#DCDCDC")};
  box-shadow: 1px 3px 5px rgba(0, 0, 0, 0.07);
  border-radius: 4px;

  ${media.tablet`
    min-width: 375px;
    max-width: 400px;
    top: 50px;
    ${(props: StyledDropdownContentProps) =>
      props.theme.scriptDirection === "RTL"
        ? `
      left: 0;
      right: 0px;
    `
        : `
      left: 0px;
      right: 0;
    `}
  `};
  ${media.desktopEmbedSafe`
  /* change menu location base on text direction */
    margin: unset;
    bottom: ${(props: StyledDropdownContentProps) =>
      props.bottom ? `${props.bottom}px` : "unset"};
  `};
  a {
    text-decoration: none;
  }
`;

const StyledDropdownTrigger = styled.div`
  display: flex;
  align-items: center;
  padding: 0;
  cursor: pointer;
  font-weight: 700;
  font-size: 0.9em;
  transition: color 0.5s;
  z-index: 100;
  white-space: nowrap;
  direction: ${props => props.theme.scriptDirection || "ltr"};
`;

const StyledDropDownArrowIcon = styled(DropDownArrowIcon)`
  vertical-align: middle;
  padding: 0px 4px;
  fill: ${props => props.theme.appBarText};
`;

const StyledDropDownArrowUpIcon = styled(StyledDropDownArrowIcon)`
  transform: rotate(180deg);
`;

const Dropdown: React.FC<DropdownProps> = ({
  children,
  isOpen = false,
  toggle = noOp
}) => (
  <div>
    {React.Children.map(children, child =>
      React.cloneElement(child as React.ReactElement<any>, {
        isOpen,
        toggle
      })
    )}
  </div>
);

const DropdownTrigger: React.FC<DropdownTriggerProps> = ({
  toggle,
  children,
  arrow = false,
  isOpen = false,
  ...props
}) => (
  <StyledDropdownTrigger onClick={toggle} {...props}>
    {children}
    {arrow && isOpen ? <StyledDropDownArrowUpIcon size="11" /> : null}
    {arrow && !isOpen ? <StyledDropDownArrowIcon size="11" /> : null}
  </StyledDropdownTrigger>
);

const DropdownContent: React.FC<DropdownContentProps> = ({
  isOpen = false,
  bottom = "",
  children,
  ...props
}) => (
  <StyledDropdownContent isOpen={isOpen} bottom={bottom} {...props}>
    {children}
  </StyledDropdownContent>
);

export { DropdownTrigger, DropdownContent };
export default Dropdown;
